import { useEffect, useMemo, useState } from 'react';
import {
  FileText, Scale, Home, Car, TrendingUp, Landmark, Receipt, FileSpreadsheet,
  Briefcase, HelpCircle, Check, X,
} from 'lucide-react';
import { Button, Card, Input, Select, Tag } from './ui';
import { money } from '../lib/money';
import type { IntakeProposal } from '../hooks/useStatements';

const DOC_TYPE_LABELS: Record<string, string> = {
  bank_statement: 'Bank Statement',
  credit_card_statement: 'Credit Card Statement',
  legal_document: 'Legal Document',
  real_estate_valuation: 'Real Estate Valuation',
  vehicle_valuation: 'Vehicle Valuation',
  brokerage_retirement_statement: 'Brokerage / Retirement Statement',
  mortgage_statement: 'Mortgage Statement',
  pay_stub: 'Pay Stub',
  tax_return: 'Tax Return',
  business_interest: 'Business Interest',
  unknown: 'Unrecognized Document',
};

// Document types a user can manually assign to a proposal. Bank/credit
// statements are included so a misclassified statement can be corrected and
// sent back into the statement pipeline.
const SELECTABLE_DOC_TYPES = [
  'bank_statement',
  'credit_card_statement',
  'legal_document',
  'real_estate_valuation',
  'vehicle_valuation',
  'brokerage_retirement_statement',
  'mortgage_statement',
  'pay_stub',
  'tax_return',
];

// Order + labels for the legal case fields a proposal can fill.
const LEGAL_FIELD_ORDER: Array<{ key: string; label: string }> = [
  { key: 'cause_number', label: 'Cause Number' },
  { key: 'court', label: 'Court' },
  { key: 'county', label: 'County' },
  { key: 'client_name', label: 'Client Name' },
  { key: 'opposing_party_name', label: 'Opposing Party' },
  { key: 'client_role', label: 'Client Role' },
  { key: 'marriage_date', label: 'Marriage Date' },
  { key: 'separation_date', label: 'Separation Date' },
  { key: 'opposing_counsel_name', label: 'Opposing Counsel' },
  { key: 'opposing_counsel_firm', label: 'Counsel Firm' },
  { key: 'opposing_counsel_address', label: 'Counsel Address' },
  { key: 'opposing_counsel_email', label: 'Counsel Email' },
  { key: 'filing_date', label: 'Filing Date' },
  { key: 'trial_date', label: 'Trial Date' },
  { key: 'mediation_date', label: 'Mediation Date' },
  { key: 'temp_orders_date', label: 'Temporary Orders Date' },
  { key: 'divorce_decree_date', label: 'Decree Date' },
];

function docTypeIcon(docType: string) {
  switch (docType) {
    case 'legal_document': return Scale;
    case 'real_estate_valuation': return Home;
    case 'vehicle_valuation': return Car;
    case 'brokerage_retirement_statement': return TrendingUp;
    case 'mortgage_statement': return Landmark;
    case 'pay_stub': return Receipt;
    case 'tax_return': return FileSpreadsheet;
    case 'business_interest': return Briefcase;
    case 'unknown': return HelpCircle;
    default: return FileText;
  }
}

// Doc types whose confirm card shows a proposed asset/liability record.
const ASSET_LANE_TYPES = new Set([
  'real_estate_valuation',
  'vehicle_valuation',
  'brokerage_retirement_statement',
  'mortgage_statement',
  'business_interest',
]);

// Income doc types whose confirm card shows per-spouse income facts.
const INCOME_LANE_TYPES = new Set(['pay_stub', 'tax_return']);

const VALUE_SOURCE_LABELS: Record<string, string> = {
  zestimate: 'Zillow Zestimate',
  redfin: 'Redfin Estimate',
  appraisal: 'Appraisal',
  tax_appraisal: 'Tax Appraisal',
  estimate: 'Estimate',
  kbb: 'Kelley Blue Book',
  edmunds: 'Edmunds',
  nada: 'NADA',
  statement: 'Statement',
  manual: 'Manual',
};

// Ordered display rows per asset/liability doc type. `money` formats as USD.
const ASSET_FIELD_ORDER: Record<string, Array<{ key: string; label: string; money?: boolean; source?: boolean }>> = {
  real_estate_valuation: [
    { key: 'address', label: 'Address' },
    { key: 'estimated_value', label: 'Estimated Value', money: true },
    { key: 'legal_description', label: 'Legal Description' },
    { key: 'value_source', label: 'Value Source', source: true },
  ],
  vehicle_valuation: [
    { key: 'name', label: 'Vehicle' },
    { key: 'vehicle_vin', label: 'VIN' },
    { key: 'estimated_value', label: 'Estimated Value', money: true },
    { key: 'value_source', label: 'Value Source', source: true },
  ],
  brokerage_retirement_statement: [
    { key: 'name', label: 'Account' },
    { key: 'asset_type', label: 'Type' },
    { key: 'estimated_value', label: 'Value', money: true },
    { key: 'retirement_plan_type', label: 'Plan Type' },
    { key: 'retirement_employer', label: 'Employer' },
  ],
  mortgage_statement: [
    { key: 'creditor_name', label: 'Lender' },
    { key: 'balance', label: 'Balance Owed', money: true },
    { key: 'account_last_four', label: 'Account' },
    { key: 'property_address', label: 'Property' },
    { key: 'escrow_balance', label: 'Escrow Balance', money: true },
  ],
  business_interest: [
    { key: 'name', label: 'Business' },
    { key: 'asset_type', label: 'Type' },
    { key: 'estimated_value', label: 'Estimated Value', money: true },
  ],
};

// Ordered display rows per income doc type. `money` formats as USD.
const INCOME_FIELD_ORDER: Record<string, Array<{ key: string; label: string; money?: boolean }>> = {
  pay_stub: [
    { key: 'employer', label: 'Employer' },
    { key: 'pay_period', label: 'Pay Period' },
    { key: 'gross_pay', label: 'Gross (this period)', money: true },
    { key: 'net_pay', label: 'Net (this period)', money: true },
    { key: 'withholdings', label: 'Withholdings', money: true },
    { key: 'retirement_contributions', label: 'Retirement', money: true },
    { key: 'ytd_gross', label: 'YTD Gross', money: true },
    { key: 'ytd_net', label: 'YTD Net', money: true },
  ],
  tax_return: [
    { key: 'tax_year', label: 'Tax Year' },
    { key: 'filing_status', label: 'Filing Status' },
    { key: 'annual_gross_income', label: 'Annual Income', money: true },
  ],
};

const PAY_PERIOD_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  semimonthly: 'Semimonthly',
  monthly: 'Monthly',
  annual: 'Annual',
};

function formatMoney(value: unknown): string {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return money(num);
}

interface ProposedAssetRow {
  label: string;
  value: string;
}

function getProposedAssetRows(proposal: IntakeProposal): ProposedAssetRow[] {
  const payload = (proposal.payload ?? {}) as { fields?: Record<string, unknown> };
  const fields = payload.fields ?? {};
  const order = ASSET_FIELD_ORDER[proposal.doc_type] ?? [];
  const rows: ProposedAssetRow[] = [];
  for (const { key, label, money, source } of order) {
    const raw = fields[key];
    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
    let value: string;
    if (money) value = formatMoney(raw);
    else if (source) value = VALUE_SOURCE_LABELS[String(raw)] ?? String(raw);
    else value = String(raw);
    rows.push({ label, value });
  }
  return rows;
}

function getProposedIncomeRows(proposal: IntakeProposal): ProposedAssetRow[] {
  const payload = (proposal.payload ?? {}) as { fields?: Record<string, unknown> };
  const fields = payload.fields ?? {};
  const order = INCOME_FIELD_ORDER[proposal.doc_type] ?? [];
  const rows: ProposedAssetRow[] = [];
  for (const { key, label, money } of order) {
    const raw = fields[key];
    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
    let value: string;
    if (money) value = formatMoney(raw);
    else if (key === 'pay_period') value = PAY_PERIOD_LABELS[String(raw)] ?? String(raw);
    else value = String(raw);
    rows.push({ label, value });
  }
  return rows;
}

interface MatchHint {
  action?: string;
  name?: string | null;
}

interface ProposedLegalField {
  key: string;
  label: string;
  proposed: string;
  current: string | null;
  conflict: boolean;
}

function getProposedLegalFields(
  proposal: IntakeProposal,
  caseFields: Record<string, unknown>,
): ProposedLegalField[] {
  const payload = (proposal.payload ?? {}) as { fields?: Record<string, unknown> };
  const fields = payload.fields ?? {};
  const current = caseFields ?? {};
  const out: ProposedLegalField[] = [];
  for (const { key, label } of LEGAL_FIELD_ORDER) {
    const raw = fields[key];
    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
    const proposed = String(raw);
    const curRaw = current[key];
    const curVal = curRaw === null || curRaw === undefined || String(curRaw).trim() === ''
      ? null : String(curRaw);
    const conflict = curVal !== null && curVal !== proposed;
    out.push({ key, label, proposed, current: curVal, conflict });
  }
  return out;
}

interface IntakeProposalCardProps {
  proposal: IntakeProposal;
  caseFields?: Record<string, unknown>;
  onConfirm: (
    id: number,
    applyFields?: string[],
    spouse?: string,
    incomeOverrides?: Record<string, unknown>,
  ) => void;
  onDismiss: (id: number) => void;
  onReclassify: (id: number, docType: string) => void;
  busy?: boolean;
}

/** Segmented spouse-choice pill shared by the three toggle buttons. */
function spouseToggleClass(selected: boolean): string {
  return selected
    ? 'border-green-600 bg-green-100 text-green-900 hover:bg-green-100'
    : 'font-normal text-ink-soft hover:border-green-400';
}

export default function IntakeProposalCard({
  proposal, caseFields = {}, onConfirm, onDismiss, onReclassify, busy = false,
}: IntakeProposalCardProps) {
  const isUnknown = proposal.doc_type === 'unknown';
  const isLegal = proposal.doc_type === 'legal_document';
  const isAssetLane = ASSET_LANE_TYPES.has(proposal.doc_type);
  const isIncomeLane = INCOME_LANE_TYPES.has(proposal.doc_type);
  const [selectedType, setSelectedType] = useState('');
  const [changingType, setChangingType] = useState(false);
  const Icon = docTypeIcon(proposal.doc_type);
  const label = DOC_TYPE_LABELS[proposal.doc_type] ?? proposal.doc_type;
  const showTypePicker = isUnknown || changingType;

  const legalFields = useMemo(
    () => (isLegal ? getProposedLegalFields(proposal, caseFields) : []),
    [isLegal, proposal, caseFields],
  );

  const assetRows = useMemo(
    () => (isAssetLane ? getProposedAssetRows(proposal) : []),
    [isAssetLane, proposal],
  );
  const incomeRows = useMemo(
    () => (isIncomeLane ? getProposedIncomeRows(proposal) : []),
    [isIncomeLane, proposal],
  );
  const matchHint = ((proposal.payload ?? {}) as { match_hint?: MatchHint }).match_hint;
  const willUpdate = matchHint?.action === 'update';

  // Spouse selector for income docs. A married-filing-jointly return is both
  // spouses' income, so it defaults to Joint; otherwise the extractor's hint,
  // else client.
  const spouseHint = ((proposal.payload ?? {}) as { spouse_hint?: string | null }).spouse_hint;
  const incomeFields = ((proposal.payload ?? {}) as { fields?: Record<string, unknown> }).fields ?? {};
  const filingStatusLower = String(incomeFields.filing_status ?? '').toLowerCase();
  const [spouse, setSpouse] = useState<string>(
    filingStatusLower.includes('joint') ? 'joint'
      : spouseHint === 'opposing' ? 'opposing' : 'client',
  );
  const clientLabel = (caseFields.client_name as string) || 'Client';
  const opposingLabel = (caseFields.opposing_party_name as string) || 'Opposing Party';

  // Manual entry: extraction failing must never dead-end OR gate an income
  // doc. Typing the numbers is optional — with nothing extracted and nothing
  // typed, confirm files the document without recording income (the server
  // treats it as a file-only action, never a 422).
  const isTaxReturn = proposal.doc_type === 'tax_return';
  // Whether confirm records real income (vs file-only) is the SERVER's call —
  // it sends has_income_fact so this card never mirrors the qualifying list.
  const hasExtractedQualifying = proposal.has_income_fact === true;
  const showManualEntry = isTaxReturn && !hasExtractedQualifying;
  const [manualIncome, setManualIncome] = useState('');
  const [manualYear, setManualYear] = useState('');
  const manualIncomeNum = Number(manualIncome.replace(/[$,\s]/g, ''));
  // Positive only: a typed 0 or negative would sit on the profile looking
  // recorded while the child-support worksheet ignores it.
  const manualIncomeValid =
    manualIncome.trim() !== '' && Number.isFinite(manualIncomeNum) && manualIncomeNum > 0;
  const manualYearValid = manualYear.trim() === '' || /^\d{4}$/.test(manualYear.trim());
  // Only ever blocked by INVALID typed input, never by absent input.
  const canRecordIncome =
    manualYearValid && (manualIncome.trim() === '' || manualIncomeValid);
  // Nothing extracted and nothing typed: confirming files the document only.
  const filingOnly = !hasExtractedQualifying && !manualIncomeValid;

  const handleIncomeConfirm = () => {
    const overrides = showManualEntry && manualIncomeValid
      ? {
          annual_gross_income: manualIncomeNum,
          ...(manualYear.trim() ? { tax_year: Number(manualYear.trim()) } : {}),
        }
      : undefined;
    onConfirm(proposal.id, undefined, spouse, overrides);
  };

  // Default: apply fields that don't conflict with an existing case value.
  // Conflicting fields start unchecked so we never silently overwrite.
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const f of legalFields) init[f.key] = !f.conflict;
    return init;
  });

  // Re-sync defaults if the case data (and thus conflict flags) loads/changes
  // after the card first mounts, so conflicting fields stay unchecked.
  const conflictSignature = legalFields.map((f) => `${f.key}:${f.conflict}`).join('|');
  useEffect(() => {
    const init: Record<string, boolean> = {};
    for (const f of legalFields) init[f.key] = !f.conflict;
    setChecked(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictSignature]);

  const toggleField = (key: string, value: boolean) =>
    setChecked((prev) => ({ ...prev, [key]: value }));

  const handleLegalConfirm = () => {
    const applyFields = legalFields.filter((f) => checked[f.key]).map((f) => f.key);
    onConfirm(proposal.id, applyFields);
  };

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-clay-100 text-clay-700">
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-green-900">{label}</span>
            {!isUnknown && (
              <Tag tone={proposal.confidence >= 80 ? 'good' : 'warn'}>
                {proposal.confidence}% confident
              </Tag>
            )}
          </div>
          {proposal.source_filename && (
            <p className="mt-1 truncate text-sm text-ink-faint" title={proposal.source_filename}>
              {proposal.source_filename}
            </p>
          )}
          {proposal.classifier_note && (
            <p className="mt-1 text-sm text-ink-soft">{proposal.classifier_note}</p>
          )}

          {showTypePicker ? (
            <div className="mt-3">
              <p className="mb-2 text-sm text-ink-soft">
                {isUnknown
                  ? "We couldn't tell what this is. What kind of document is it?"
                  : 'What kind of document is this?'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={busy}
                  size="md"
                  placeholder="Select a type…"
                  options={SELECTABLE_DOC_TYPES.map((t) => ({ value: t, label: DOC_TYPE_LABELS[t] }))}
                  className="w-64"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!selectedType || busy}
                  onClick={() => { onReclassify(proposal.id, selectedType); setChangingType(false); }}
                >
                  Set type
                </Button>
                {!isUnknown && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => { setChangingType(false); setSelectedType(''); }}
                  >
                    Cancel
                  </Button>
                )}
                {isUnknown && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={X}
                    disabled={busy}
                    onClick={() => onDismiss(proposal.id)}
                  >
                    Dismiss
                  </Button>
                )}
              </div>
            </div>
          ) : isLegal ? (
            <div className="mt-3">
              {legalFields.length === 0 ? (
                <p className="text-sm text-ink-soft">
                  We couldn't read any case details from this document. You can dismiss it or
                  change its type.
                </p>
              ) : (
                <>
                  <p className="mb-2 text-sm text-ink-soft">
                    Choose which case details to fill in. Fields that already have a value are shown
                    so you can decide before overwriting.
                  </p>
                  <div className="overflow-hidden rounded-md border border-border">
                    {/* eslint-disable-next-line no-restricted-syntax -- checkbox-per-row review table; restyled in place, DataTable cannot host it */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="w-10 px-2 py-2"></th>
                          <th className="t-eyebrow px-2 py-2 text-left">Field</th>
                          <th className="t-eyebrow px-2 py-2 text-left">Current</th>
                          <th className="t-eyebrow px-2 py-2 text-left">Proposed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {legalFields.map((f) => (
                          <tr key={f.key} className="border-t border-border-subtle">
                            <td className="px-2 py-2 align-top">
                              {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                              <input
                                type="checkbox"
                                checked={!!checked[f.key]}
                                disabled={busy}
                                onChange={(e) => toggleField(f.key, e.target.checked)}
                                className="h-4 w-4 rounded border-border-strong accent-green-700"
                              />
                            </td>
                            <td className="px-2 py-2 align-top text-ink-soft">{f.label}</td>
                            <td className="px-2 py-2 align-top">
                              {f.current ? (
                                <span className="text-ink-faint">{f.current}</span>
                              ) : (
                                <span className="text-ink-faint">—</span>
                              )}
                            </td>
                            <td className="px-2 py-2 align-top">
                              <span className={f.conflict ? 'font-medium text-clay-700' : 'text-green-900'}>
                                {f.proposed}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  icon={Check}
                  loading={busy}
                  onClick={handleLegalConfirm}
                >
                  {legalFields.length === 0 ? 'Confirm' : 'Apply selected'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setChangingType(true)}
                >
                  Change type
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  disabled={busy}
                  onClick={() => onDismiss(proposal.id)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ) : isAssetLane ? (
            <div className="mt-3">
              {assetRows.length === 0 ? (
                <p className="text-sm text-ink-soft">
                  We couldn't read the details from this document. You can dismiss it or
                  change its type.
                </p>
              ) : (
                <>
                  <p className="mb-2 text-sm text-ink-soft">
                    {willUpdate
                      ? `This matches "${matchHint?.name ?? 'an existing item'}" — confirming updates it.`
                      : 'Confirm to add this to your list.'}
                  </p>
                  <div className="overflow-hidden rounded-md border border-border">
                    {/* eslint-disable-next-line no-restricted-syntax -- key-value review table; restyled in place, DataTable chrome would double-border it */}
                    <table className="w-full text-sm">
                      <tbody>
                        {assetRows.map((r) => (
                          <tr key={r.label} className="border-t border-border-subtle first:border-t-0">
                            <td className="t-eyebrow w-40 px-2 py-2 align-top">
                              {r.label}
                            </td>
                            <td className="px-2 py-2 align-top text-green-900">{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  icon={Check}
                  loading={busy}
                  disabled={assetRows.length === 0}
                  onClick={() => onConfirm(proposal.id)}
                >
                  {willUpdate ? 'Update' : 'Add'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setChangingType(true)}
                >
                  Change type
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  disabled={busy}
                  onClick={() => onDismiss(proposal.id)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ) : isIncomeLane ? (
            <div className="mt-3">
              <div className="mb-3">
                <p className="mb-2 text-sm text-ink-soft">Whose income is this?</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    disabled={busy}
                    onClick={() => setSpouse('client')}
                    className={spouseToggleClass(spouse === 'client')}
                  >
                    {clientLabel}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    disabled={busy}
                    onClick={() => setSpouse('opposing')}
                    className={spouseToggleClass(spouse === 'opposing')}
                  >
                    {opposingLabel}
                  </Button>
                  {/* Only a jointly-FILED return is "both spouses' income";
                      a pay stub is one earner's, so Joint stays hidden. */}
                  {isTaxReturn && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="md"
                      disabled={busy}
                      onClick={() => setSpouse('joint')}
                      title="A jointly-filed return is both spouses' income — recorded on both profiles pending allocation."
                      className={spouseToggleClass(spouse === 'joint')}
                    >
                      Joint (both)
                    </Button>
                  )}
                </div>
              </div>
              {incomeRows.length === 0 && !showManualEntry ? (
                <p className="text-sm text-ink-soft">
                  We couldn't read income details from this document. You can file it
                  as-is (no income recorded), dismiss it, or change its type.
                </p>
              ) : incomeRows.length > 0 ? (
                <>
                  <p className="mb-2 text-sm text-ink-soft">
                    Confirm to record this income on the selected spouse's profile.
                  </p>
                  <div className="overflow-hidden rounded-md border border-border">
                    {/* eslint-disable-next-line no-restricted-syntax -- key-value review table; restyled in place, DataTable chrome would double-border it */}
                    <table className="w-full text-sm">
                      <tbody>
                        {incomeRows.map((r) => (
                          <tr key={r.label} className="border-t border-border-subtle first:border-t-0">
                            <td className="t-eyebrow w-44 px-2 py-2 align-top">
                              {r.label}
                            </td>
                            <td className="px-2 py-2 align-top text-green-900">{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
              {showManualEntry && (
                <div className="mt-2">
                  <p className="mb-2 text-sm text-ink-soft">
                    {incomeRows.length === 0
                      ? 'The income on this return could not be read automatically. File it as-is, or enter the numbers below (optional):'
                      : 'The income amount could not be read automatically. File it as-is, or enter it below (optional):'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={manualIncome}
                      disabled={busy}
                      onChange={(e) => setManualIncome(e.target.value)}
                      placeholder="Total income (annual)"
                      size="md"
                      className="w-44"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={manualYear}
                      disabled={busy}
                      onChange={(e) => setManualYear(e.target.value)}
                      placeholder="Tax year"
                      size="md"
                      className={manualYearValid ? 'w-24' : 'w-24 border-danger'}
                    />
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  icon={Check}
                  loading={busy}
                  disabled={!canRecordIncome}
                  onClick={handleIncomeConfirm}
                >
                  {filingOnly ? `File as ${label.toLowerCase()}` : 'Record income'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setChangingType(true)}
                >
                  Change type
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  disabled={busy}
                  onClick={() => onDismiss(proposal.id)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                icon={Check}
                loading={busy}
                onClick={() => onConfirm(proposal.id)}
              >
                Confirm
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setChangingType(true)}
              >
                Change type
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={X}
                disabled={busy}
                onClick={() => onDismiss(proposal.id)}
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
